import React, { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { supabase, TABLES } from '../../config/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Card from '../ui/Card'
import Button from '../ui/Button'
import Switch from '../ui/Switch'
import Badge from '../ui/Badge'
import toast from 'react-hot-toast'

const { FiShield, FiEye, FiDatabase, FiDownload, FiTrash2, FiLock } = FiIcons

const PrivacySettings = () => {
  const { family, setFamily } = useAuth()
  const [loading, setLoading] = useState(false)
  const [settings, setSettings] = useState(
    family?.settings?.privacy || {
      analytics_tracking: true,
      data_sharing: false,
      public_leaderboard: false,
      anonymous_usage_stats: true,
      marketing_emails: false,
      third_party_integrations: false
    }
  )

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const saveSettings = async () => {
    setLoading(true)
    try {
      const updatedSettings = {
        ...family.settings,
        privacy: settings
      }

      const { data, error } = await supabase
        .from(TABLES.FAMILIES)
        .update({ settings: updatedSettings })
        .eq('id', family.id)
        .select()
        .single()

      if (error) {
        console.error('Settings update error:', error)
        toast.error('Failed to save privacy settings')
      } else {
        setFamily(data)
        toast.success('Privacy settings saved! 🔒')
      }
    } catch (error) {
      console.error('Unexpected error:', error)
      toast.error('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const requestDataExport = async () => {
    toast.success('Data export request submitted! You\'ll receive an email with your data within 24 hours.')
  }

  const privacyOptions = [
    {
      key: 'analytics_tracking',
      title: 'Analytics Tracking',
      description: 'Allow us to collect usage analytics to improve the app',
      icon: FiDatabase,
      color: 'text-blue-500',
      recommended: true
    },
    {
      key: 'anonymous_usage_stats',
      title: 'Anonymous Usage Statistics',
      description: 'Share anonymous usage data to help improve features',
      icon: FiEye,
      color: 'text-green-500',
      recommended: true
    },
    {
      key: 'data_sharing',
      title: 'Data Sharing with Partners',
      description: 'Allow sharing anonymized data with trusted partners',
      icon: FiDatabase,
      color: 'text-orange-500',
      recommended: false
    },
    {
      key: 'public_leaderboard',
      title: 'Public Leaderboard',
      description: 'Show your family on public leaderboards (anonymous)',
      icon: FiEye,
      color: 'text-purple-500',
      recommended: false
    },
    {
      key: 'marketing_emails',
      title: 'Marketing Emails',
      description: 'Receive emails about new features and tips',
      icon: FiDatabase,
      color: 'text-pink-500',
      recommended: false
    },
    {
      key: 'third_party_integrations',
      title: 'Third-party Integrations',
      description: 'Allow connections with external apps and services',
      icon: FiLock,
      color: 'text-red-500',
      recommended: false
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <SafeIcon icon={FiShield} className="text-primary-500 text-xl" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Privacy Settings</h2>
            <p className="text-gray-600 mt-1">
              Control how your family's data is used and shared
            </p>
          </div>
        </div>
      </Card>

      {/* Privacy Options */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data & Privacy Controls</h3>
        <div className="space-y-4">
          {privacyOptions.map((option) => (
            <div
              key={option.key}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                <SafeIcon icon={option.icon} className={`${option.color} text-lg`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{option.title}</span>
                    {option.recommended && (
                      <Badge variant="success" size="sm">Recommended</Badge>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">{option.description}</div>
                </div>
              </div>
              <Switch
                checked={settings[option.key]}
                onChange={(checked) => handleSettingChange(option.key, checked)}
              />
            </div>
          ))}
        </div>
      </Card>

      {/* Data Management */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Management</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiDownload} className="text-blue-500 text-lg" />
              <div>
                <div className="font-medium text-gray-900">Export Your Data</div>
                <div className="text-sm text-gray-500">
                  Download a copy of all your family's data
                </div>
              </div>
            </div>
            <Button variant="outline" onClick={requestDataExport}>
              <SafeIcon icon={FiDownload} className="text-sm" />
              Request Export
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiTrash2} className="text-red-500 text-lg" />
              <div>
                <div className="font-medium text-red-800">Delete All Data</div>
                <div className="text-sm text-red-600">
                  Permanently delete your family account and all data
                </div>
              </div>
            </div>
            <Button variant="danger" size="sm">
              <SafeIcon icon={FiTrash2} className="text-sm" />
              Delete Account
            </Button>
          </div>
        </div>
      </Card>

      {/* Data Retention */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Retention Policy</h3>
        <div className="space-y-4 text-sm text-gray-600">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">What data we collect:</h4>
            <ul className="space-y-1 text-blue-700">
              <li>• Family and children profiles</li>
              <li>• Task completion history</li>
              <li>• Points and rewards data</li>
              <li>• Usage analytics (if enabled)</li>
              <li>• Account settings and preferences</li>
            </ul>
          </div>

          <div className="p-4 bg-green-50 rounded-lg">
            <h4 className="font-medium text-green-800 mb-2">How long we keep it:</h4>
            <ul className="space-y-1 text-green-700">
              <li>• Active account data: Indefinitely while account is active</li>
              <li>• Deleted account data: 30 days (for account recovery)</li>
              <li>• Anonymous analytics: Up to 2 years</li>
              <li>• Backup data: Up to 90 days</li>
            </ul>
          </div>

          <div className="p-4 bg-purple-50 rounded-lg">
            <h4 className="font-medium text-purple-800 mb-2">Your rights:</h4>
            <ul className="space-y-1 text-purple-700">
              <li>• Access your data at any time</li>
              <li>• Export your data in a portable format</li>
              <li>• Request corrections to inaccurate data</li>
              <li>• Delete your account and all associated data</li>
              <li>• Opt out of data processing for marketing</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Security Information */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Security & Encryption</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiLock} className="text-green-500" />
              <span className="font-medium text-gray-900">Data Encryption</span>
            </div>
            <p className="text-sm text-gray-600">
              All data is encrypted in transit and at rest using industry-standard encryption.
            </p>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiShield} className="text-blue-500" />
              <span className="font-medium text-gray-900">Access Controls</span>
            </div>
            <p className="text-sm text-gray-600">
              Strict access controls ensure only authorized family members can view your data.
            </p>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiDatabase} className="text-purple-500" />
              <span className="font-medium text-gray-900">Secure Storage</span>
            </div>
            <p className="text-sm text-gray-600">
              Data is stored in secure, compliant data centers with regular security audits.
            </p>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiEye} className="text-orange-500" />
              <span className="font-medium text-gray-900">Privacy by Design</span>
            </div>
            <p className="text-sm text-gray-600">
              We collect only the minimum data necessary to provide our service.
            </p>
          </div>
        </div>
      </Card>

      {/* Contact Information */}
      <Card className="bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Questions About Privacy?</h3>
        <p className="text-gray-600 mb-4">
          If you have questions about how we handle your data or want to exercise your privacy rights, 
          please don't hesitate to contact us.
        </p>
        <div className="flex gap-3">
          <Button variant="outline">
            Privacy Policy
          </Button>
          <Button variant="outline">
            Contact Support
          </Button>
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={saveSettings}
          loading={loading}
        >
          <SafeIcon icon={FiShield} className="text-sm" />
          Save Privacy Settings
        </Button>
      </div>
    </div>
  )
}

export default PrivacySettings